/**
 * «Вы: Нарвская площадь, 3 (±40 м)» — the name of the place the browser's GPS gave (INV-PH-140).
 *
 * A GPS point used to be drawn as «Выбранное положение»: the visitor could not see how the system understood their
 * place. Here the point is turned into an address by 2GIS Geocoder, in the browser, with the same key and the same
 * way as `addressSuggest.ts` (docs.2gis.com/en/api/search/geocoder/overview: `GET
 * https://catalog.api.2gis.com/3.0/items/geocode?lon&lat&fields=items.adm_div,items.address&key`, answered with
 * `result.items[]` carrying `address_name` / `full_name` / `name`).
 *
 * The key is rationed like the suggester's: one request per ~50 m cell (a visitor who taps «Определить» twice on the
 * same spot is asked about once), a hard timeout, and no retry. A failure — a timeout, a refusal, an empty answer,
 * a key that is not set — is `null`: no name, as before, and nothing on the screen about it.
 */

export const GEOCODE_URL = 'https://catalog.api.2gis.com/3.0/items/geocode';
/** The cache cell, metres: two taps within it share one answer. */
export const GRID_M = 50;
/** The answer is awaited this long before the position goes out without a name. */
export const TIMEOUT_MS = 2500;
/** The label travels as `location_label`; the server keeps ≤120 characters of public text. */
export const MAX_LABEL = 120;
/** Past this accuracy the place is «примерно»: a fix that coarse is a district, not an address. */
export const APPROXIMATE_M = 300;

const M_PER_DEG = 111_320;

export function geocodeKey(): string {
  return import.meta.env.VITE_PP_DGIS_MAPGL_KEY ?? '';
}

export function geocodeUrl(lat: number, lon: number, key: string): string {
  const params = new URLSearchParams({ lon: String(lon), lat: String(lat), fields: 'items.adm_div,items.address', locale: 'ru_RU', key });
  return `${GEOCODE_URL}?${params}`;
}

/** The ~50 m cell of a point: the cache key. Longitude degrees shrink with latitude, so the cell is square on the ground. */
export function cellKey(lat: number, lon: number): string {
  const dLat = GRID_M / M_PER_DEG;
  const dLon = GRID_M / (M_PER_DEG * Math.max(Math.cos((lat * Math.PI) / 180), 0.01));
  return `${Math.round(lat / dLat)}:${Math.round(lon / dLon)}`;
}

/** The address of the nearest object of the answer: `address_name` («Нарвская площадь, 3»), else `full_name`, else
 *  `name`. A district or a city (`adm_div`) is not an address — it would say «Санкт-Петербург» about a place. */
export function parseAddress(body: unknown): string | null {
  const items = (body as { result?: { items?: unknown } } | null)?.result?.items;
  if (!Array.isArray(items)) return null;
  for (const raw of items) {
    const item = raw as { type?: unknown; address_name?: unknown; full_name?: unknown; name?: unknown } | null;
    if (!item || item.type === 'adm_div') continue;
    for (const field of [item.address_name, item.full_name, item.name]) {
      if (typeof field === 'string' && field.trim()) return field.trim().replace(/\s+/g, ' ').slice(0, MAX_LABEL);
    }
  }
  return null;
}

export type ReverseGeocoder = (lat: number, lon: number) => Promise<string | null>;

export type ReverseOptions = { key: string; fetchFn?: typeof fetch; timeoutMs?: number };

export function createReverseGeocoder(opts: ReverseOptions): ReverseGeocoder {
  const doFetch = opts.fetchFn ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const timeoutMs = opts.timeoutMs ?? TIMEOUT_MS;
  const cache = new Map<string, string>();
  return async (lat, lon) => {
    if (!opts.key.trim() || !Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    const cell = cellKey(lat, lon);
    const cached = cache.get(cell);
    if (cached) return cached;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await doFetch(geocodeUrl(lat, lon, opts.key), { signal: controller.signal });
      if (!res.ok) return null;
      const label = parseAddress(await res.json());
      if (label) cache.set(cell, label);
      return label;
    } catch {
      return null;
    } finally {
      clearTimeout(timer);
    }
  };
}

/** The app's geocoder: the shared key, one cache for the page. */
let shared: ReverseGeocoder | null = null;
export function reverseGeocode(lat: number, lon: number): Promise<string | null> {
  shared ??= createReverseGeocoder({ key: geocodeKey() });
  return shared(lat, lon);
}

/** «Вы: Нарвская пл., 3 (±40 м)» — the card's line; accuracy rounded up to ten metres, nothing when unknown. */
export function placeLine(label: string, accuracyM: number | null): string {
  const accuracy = accuracyM != null && Number.isFinite(accuracyM) && accuracyM > 0 ? ` (±${Math.max(10, Math.round(accuracyM / 10) * 10)} м)` : '';
  return `Вы: ${label}${accuracy}`;
}

/** The honest line for a coarse fix; the card offers the departure buttons and the address beside it. */
export function approximateText(accuracyM: number): string {
  return `Место определено примерно (±${Math.round(accuracyM / 10) * 10} м). Выберите точку отправления или укажите адрес — так маршрут будет точнее.`;
}
